import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Swal from 'sweetalert2';
import { size } from "lodash";
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from "../../../../hooks";
import { Week, Syllabu } from "../../../../api";
import {
  Sidebar,
  Workspace,
  TemplateBar,
} from "../../../../components/Admin/Media/MediaEdit";
import { TemplatesManager } from "../../../../components/Admin/Media/MediaEdit/TemplateBar/TemplatesManager";
import { SlidesContextEdit } from '../../../../contexts/MediaContext';
import "./MediaEdit.scss";

const weekController = new Week();
const syllabuController = new Syllabu();

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

export function MediaEdit() {
  const { syllabuSubjectGradeId } = useParams();
  const [weeksOptions, setWeeksOptions] = useState([]);
  const [weeksAndSessions, setWeeksAndSessions] = useState([]);
  const [syllabu, setSyllabu] = useState(null);
  const [subject, setSubject] = useState(null);
  const [grade, setGrade] = useState(null);
  const [reload, setReload] = useState(true);
  const [reloadWeek, setReloadWeek] = useState(true);
  const [activeSession, setActiveSession] = useState({});
  const [slidesActiveSessionEdit, setSlidesActiveSessionEdit] = useState([]);
  const { accessToken } = useAuth();

  const onReload = () => setReload((prevState) => !prevState);
  const onReloadWeek = () => setReloadWeek((prevState) => !prevState);

  const onDragOver = (e: React.DragEvent) => e.preventDefault();

  /**
   * Fetches and updates the weeks and sessions data for a given subject grade.
   * 
   * This useEffect hook is triggered when there's a need to reload the weeks and sessions data, indicated by changes to `reload`, `reloadWeek`, `syllabuSubjectGradeId`, or `accessToken`. When `reload` or `reloadWeek` is true, it performs an asynchronous operation to fetch the weeks associated with the specified `syllabuSubjectGradeId` using the provided `accessToken`. The fetched data is then used to update the state with the new weeks and sessions information.
   * 
   * If `reloadWeek` is true, it additionally maps the fetched weeks to a format suitable for selection options (`value`, `text`, and `length` of sessions) and updates the `weeksOptions` state. It then triggers the `onReloadWeek` callback to perform any additional actions required after reloading the weeks data.
   * 
   * If `reload` is true, it triggers the `onReload` callback to perform any additional actions required after a general reload.
   * 
   * Errors encountered during the fetch operation are caught and logged to the console to aid in debugging and ensure the application does not crash.
   * 
   * @dependency reloadWeek - A boolean indicating if the weeks data should be reloaded due to a change in weeks.
   * @dependency reload - A boolean indicating if a general reload is requested.
   * @dependency syllabuSubjectGradeId - The ID of the subject grade for which weeks and sessions are being fetched.
   * @dependency accessToken - The access token required for authentication to perform the fetch operation.
   */
  useEffect(() => {
    (async () => {
      try {
        if (reload || reloadWeek) {
          const response = await weekController.getWeeksBySubject(
            accessToken,
            syllabuSubjectGradeId
          );
          setWeeksAndSessions(response);
          if (reloadWeek) {
            const options = response.map((week) => ({
              value: week.id,
              text: week.title,
              length: week.sessions.length,
            }));
            setWeeksOptions(options);
            onReloadWeek();
          }
          if (reload) onReload();
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [reloadWeek, reload, syllabuSubjectGradeId, accessToken]);

  /**
   * Fetches and updates the syllabu, subject, and grade information based on the selected syllabu subject grade ID.
   * 
   * This useEffect hook is triggered whenever the `syllabuSubjectGradeId` or `accessToken` changes. It performs an asynchronous operation to fetch the syllabu, subject, and grade information associated with the specified `syllabuSubjectGradeId` using the provided `accessToken`. Upon successfully fetching the data, it updates the component's state with the `syllabuId`, `subjectId`, and `gradeId` from the response.
   * 
   * If an error occurs during the fetch operation, the error is caught and logged to the console. This ensures that the application can handle the error gracefully without crashing.
   * 
   * @dependency syllabuSubjectGradeId - The ID used to fetch the syllabu, subject, and grade information.
   * @dependency accessToken - The access token required for authentication to perform the fetch operation.
   */
  useEffect(() => {
    (async () => {
      try {
        const response = await syllabuController.getSubjectGradeBySyllabu(
          accessToken,
          syllabuSubjectGradeId
        );
        setSyllabu(response.Syllabu);
        setSubject(response.subject);
        setGrade(response.Grade);
      } catch (error) {
        console.error("Error al cargar información de la asignatura:", error);
        Toast.fire({
          icon: "error",
          title: "Error al cargar la información de la asignatura. Por favor, contacta al administrador.",
        });
      }
    })();
  }, [syllabuSubjectGradeId, accessToken]);

  /**
   * Sets the active session for editing and updates the active slides accordingly.
   * 
   * This function is responsible for setting the currently active session in the media editing context. It updates the `activeSession` state with the provided session object. If the session is valid (not null or undefined), it checks if the session contains slides. If the session has no slides or the `slides` property is undefined, it sets the `slidesActiveSessionEdit` state to an empty array. Otherwise, it updates `slidesActiveSessionEdit` with the slides from the active session.
   * 
   * This ensures that the editing context is always in sync with the selected session, allowing for accurate editing of session slides.
   * 
   * @param session - The session object to set as the active session for editing.
   */
  const setActiveSessionEdit = (session) => {
    setActiveSession(session);
    if (session) {
      setSlidesActiveSessionEdit(
        size(session.slides) === 0 || session.slides === undefined
          ? []
          : session.slides
      );
    }
  };

  /**
   * Handles the drag event for a template in the media editing context.
   * 
   * This function is designed to be used in a drag-and-drop interface for templates within a media editing application. When a template is dragged, this function checks if there is an active session. If an active session exists, it uses the `dataTransfer` object from the event to set the `templateId` of the dragged template. This allows the `templateId` to be accessed by the drop handler to perform further actions, such as adding the template to the active session.
   * 
   * If there is no active session, it logs a message indicating that no active session is available. This is useful for debugging and ensuring that templates can only be dragged when an active session is present.
   * 
   * @param e - The drag event object.
   * @param templateId - The unique identifier of the template being dragged.
   */
  const onDragTemplate = (e: React.DragEvent, templateId: string) => {
    if (activeSession) {
      e.dataTransfer.setData("templateId", templateId);
    } else {
      console.log("No active Session");
    }
  };

  /**
   * Searches for a slide configuration by its name within the multimedia and question templates.
   * 
   * This function iterates over two sets of templates managed by `TemplatesManager`: `multimediaTemplates` and `questionTemplates`. It looks for a template whose `component` property matches the provided `slideName`. The search is performed first on the multimedia templates, followed by the question templates.
   * 
   * If a matching template is found, it is returned immediately, thus prioritizing multimedia templates over question templates in cases where a name might overlap. If no matching template is found in either set, the function returns `null`.
   * 
   * This approach allows for a flexible search mechanism across different types of templates, facilitating the retrieval of specific slide configurations for use in media editing contexts.
   * 
   * @param slideName - The name of the slide component to search for within the templates.
   * @returns The found template configuration object or `null` if no matching template is found.
   */
  const onSearchConfigSlide = (slideName) => {
    for (const multimedia of TemplatesManager.multimediaTemplates) {
      if (multimedia.component === slideName) return multimedia;
    }
    for (const question of TemplatesManager.questionTemplates) {
      if (question.component === slideName) return question;
    }
    return null;
  };

  /**
   * Handles the drop event for templates in the media editing context.
   * 
   * This function is triggered when a template is dropped into a designated drop zone within the media editing interface. It checks if there is an active session by verifying the presence and non-emptiness of the `_id` property of the `activeSession` object. If an active session exists, it proceeds to handle the dropped template.
   * 
   * The function retrieves the `templateId` from the event's `dataTransfer` object, which was set during the drag event of the template. It then searches for the corresponding template configuration using the `onSearchConfigSlide` function, passing the `templateId` as an argument.
   * 
   * If a template configuration is found (`configCopy`), a new slide item is created with a unique `_id` generated by `uuidv4`, the `templateId`, and the template content (`configCopy.body`). This new slide item is then added to the end of the `slidesActiveSessionEdit` array, effectively updating the list of slides for the active session.
   * 
   * Upon successful addition of the new slide, a success notification is displayed using `Toast.fire`, indicating the successful addition of the slide and its sequence number within the session.
   * 
   * If there is no active session, a warning notification is displayed, indicating the absence of an active session and thus the inability to add the template to the session.
   */
  const onDropTemplate = (e: React.DragEvent) => {
    if (activeSession.id && activeSession.id !== "") {
      const template = e.dataTransfer.getData("templateId");
      const content = onSearchConfigSlide(template);
      const configCopy = JSON.parse(JSON.stringify(content)); //* Copia profunda de la configuración de template
      if (configCopy) { //* Si se encontró la configuración del template, sino es arrastre de archivo imagen o audio a media
        const newItem = {
          _id: uuidv4(),
          template: template,
          content: configCopy.body
        };
        setSlidesActiveSessionEdit([...slidesActiveSessionEdit, newItem]);
        Toast.fire({
          icon: "success",
          title: `Se agregó hoja ${slidesActiveSessionEdit.length + 1} con éxito.`
        });
      }
    } else {
      Toast.fire({
        icon: "warning",
        title: `No hay sesión activa.`
      });
    }
  };

  /**
   * Updates a specific slide within the active session's slides array.
   * 
   * This function is designed to update the content or properties of a slide at a specified index within the `slidesActiveSessionEdit` state, which represents the slides of the currently active session in a media editing context. It takes an updated slide object and the index of the slide to be updated as arguments.
   * 
   * The function works by first creating a shallow copy of the current slides array to maintain immutability. It then replaces the slide at the specified index with the updated slide object. Finally, it updates the `slidesActiveSessionEdit` state with the new array of slides, ensuring that the state reflects the latest changes.
   * 
   * @param updatedSlide - The slide object with updated content or properties.
   * @param index - The index of the slide in the `slidesActiveSessionEdit` array to be updated.
   */
  const onUpdateSlide = (updatedSlide, index) => {
    setSlidesActiveSessionEdit((prevSlides) => {
      const newSlides = [...prevSlides];
      newSlides[index] = updatedSlide;
      return newSlides;
    });
  };

  return (
    <SlidesContextEdit.Provider value={{ slidesActiveSessionEdit, setSlidesActiveSessionEdit }}>
      <div className="media-edit-page">
        <Sidebar
          weeks={weeksAndSessions}
          weeksOptions={weeksOptions}
          setWeeksOptions={setWeeksOptions}
          grade={grade}
          subject={subject}
          syllabu={syllabu}
          activeSession={activeSession}
          setActiveSessionEdit={setActiveSessionEdit}
          reload={reload}
          onReload={onReload}
          reloadWeek={reloadWeek}
          onReloadWeek={onReloadWeek}
        />
        <Workspace
          weeks={weeksAndSessions}
          weeksOptions={weeksOptions}
          setWeeksAndSessions={setWeeksAndSessions}
          activeSession={activeSession}
          setActiveSessionEdit={setActiveSessionEdit}
          onDropTemplate={onDropTemplate}
          onDragOver={onDragOver}
          onUpdateSlide={onUpdateSlide}
          onReload={onReload}
          onReloadWeek={onReloadWeek}
        />
        <TemplateBar onDragTemplate={onDragTemplate} />
      </div>
    </SlidesContextEdit.Provider>
  );
}
